import {
  Boxes,
  ChevronsDownUp,
  ChevronsUpDown,
  Filter,
  GitCompareArrows,
  ImageDown,
  LogOut,
  Map as MapIcon,
  Moon,
  Network,
  Palette,
  RefreshCw,
  Search,
  Sun,
  Table2 as TableIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { COLOR_BY_OPTIONS } from '../lib/colors';
import { currentSession, onSessionChange, signOut } from '../lib/auth';
import { nextTheme, resolveTheme } from '../lib/theme';
import { cn, relativeTime } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { ScenarioMenu } from './ScenarioMenu';
import { Viewers } from './Viewers';
import { Badge, IconButton, Kbd } from './ui';

export function TopBar() {
  const meta = useAppStore((s) => s.meta);
  const stats = useAppStore((s) => s.stats);
  const summary = useAppStore((s) => s.summary);
  const theme = useAppStore((s) => s.theme);
  const refreshing = useAppStore((s) => s.refreshing);
  const snowflakeConfigured = useAppStore((s) => s.snowflakeConfigured);
  const showMinimap = useAppStore((s) => s.showMinimap);
  const onlyChanged = useAppStore((s) => s.onlyChanged);
  const departmentFilter = useAppStore((s) => s.departmentFilter);
  const squadFilter = useAppStore((s) => s.squadFilter);
  const colorBy = useAppStore((s) => s.colorBy);
  const panelOpen = useAppStore((s) => s.panelOpen);
  const live = useAppStore((s) => s.live);
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);

  const setTheme = useAppStore((s) => s.setTheme);

  // Read once and kept in step, because signing out has to make it disappear.
  const [session, setSession] = useState(currentSession);
  useEffect(() => onSessionChange(setSession), []);
  const refreshOrg = useAppStore((s) => s.refreshOrg);
  const expandAll = useAppStore((s) => s.expandAll);
  const expandAllGroups = useAppStore((s) => s.expandAllGroups);
  const collapseAllGroups = useAppStore((s) => s.collapseAllGroups);
  const collapseToDepth = useAppStore((s) => s.collapseToDepth);
  const toggleMinimap = useAppStore((s) => s.toggleMinimap);
  const setPalette = useAppStore((s) => s.setPalette);
  const openPanel = useAppStore((s) => s.openPanel);
  const togglePanel = useAppStore((s) => s.togglePanel);
  const exportPng = useAppStore((s) => s.exportPng);
  const exporting = useAppStore((s) => s.exporting);

  /** The two views that are canvases; the people table folds nothing. */
  const canvas = view === 'reporting' || view === 'teams';
  const orgAge = `The org was last refreshed ${relativeTime(meta?.syncedAt ?? null)}.`;

  const [filterOpen, setFilterOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);

  /*
   * Below `sm` the header wraps onto two rows — the chart controls drop to
   * their own line and the search box collapses to the icon that opens the
   * same palette — so a phone gets every control rather than a clipped
   * toolbar. The `sm:order-*` classes put it back in reading order on a wide
   * screen, where it is a single row again.
   */

  return (
    <header className="relative z-30 flex min-h-14 shrink-0 flex-wrap items-center gap-x-2 gap-y-2 border-b border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2 sm:gap-2">
      <div className="flex shrink-0 items-center gap-2.5">
        {/*
          The mark itself, generated from the official pin by `npm run icons`
          rather than drawn here. The tile and its gradient are part of the file,
          so this is an image and not a coloured box with a glyph in it — one
          fewer place for the brand to be almost right.
        */}
        <img src="/tive-mark.svg" alt="" aria-hidden width={32} height={32} className="size-8 shrink-0" />
        <div className="hidden leading-tight md:block">
          <p className="font-display text-[13px] font-semibold text-[var(--color-ink)]">Org Planning</p>
          {/*
            How old the org is, on the line that already counts the org.
            
            It was an icon of its own at the far end of the toolbar, which is a
            control that does nothing sitting among controls that do. The
            headcount is the other fact about the snapshot as a whole, so the
            two belong on one line — and "layers" stays the word for how deep
            the reporting tree goes, because "levels" already means the grouping
            levels on the team board and one word for two things is worse than
            an imperfect word for one.
          */}
          <p
            className="flex items-center gap-1.5 text-[10.5px] text-[var(--color-ink-3)]"
            title={orgAge}
            aria-label={stats ? orgAge : undefined}
          >
            {stats ? `${stats.headcount} people · ${stats.layers} layers` : 'Loading…'}
            {live ? (
              <span
                title="Live — changes made by Claude appear instantly"
                className="flex items-center gap-1"
              >
                <span className="size-1.5 rounded-full bg-[var(--color-fresh)]" />
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mx-1 hidden h-7 w-px bg-[var(--color-line)] md:block" />

      <div className="min-w-0 flex-1 sm:flex-none">
        <ScenarioMenu />
      </div>

      {/* Beside the plan it is about, the way a shared document does it. */}
      <Viewers />

      {/* Who reports to whom, or who works with whom. Same people, same
          scenario, two ways of cutting it. */}
      <div
        role="group"
        aria-label="View"
        className="flex shrink-0 items-center gap-0.5 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] p-0.5"
      >
        <ViewTab
          icon={<Network size={14} />}
          label="Reporting"
          active={view === 'reporting'}
          onClick={() => setView('reporting')}
        />
        <ViewTab
          icon={<Boxes size={14} />}
          label="Teams"
          active={view === 'teams'}
          onClick={() => setView('teams')}
        />
        <ViewTab
          icon={<TableIcon size={14} />}
          label="People"
          active={view === 'people'}
          onClick={() => setView('people')}
        />
      </div>

      {/* One control, not two: below `sm` the label and shortcut drop away and
          it collapses to the magnifier, which opens the same palette. It also
          gives up width first when the row is tight, since it stretches again
          the moment there is room. */}
      <button
        type="button"
        aria-label="Find anyone"
        onClick={() => setPalette(true)}
        className="group flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-2.5 text-left text-[12.5px] text-[var(--color-ink-3)] transition-colors hover:border-[var(--color-line-strong)] sm:order-1 sm:min-w-[120px] sm:flex-1 sm:shrink sm:justify-start"
      >
        <Search size={14} />
        <span className="hidden flex-1 truncate sm:inline">Find anyone…</span>
        <span className="hidden sm:inline">
          <Kbd>Ctrl K</Kbd>
        </span>
      </button>

      {/* Kept ahead of the chart controls in the DOM so it stays on the first
          row when they wrap; sm:order-4 puts it back on the right on desktop. */}
      <button
        type="button"
        onClick={() => (panelOpen ? togglePanel() : openPanel('changes'))}
        className={cn(
          'flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-[12.5px] font-semibold transition-colors sm:order-4',
          summary.total > 0
            ? 'bg-[var(--color-brand-fill)] text-white hover:brightness-110'
            : 'border border-[var(--color-line)] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)]',
        )}
      >
        <GitCompareArrows size={15} />
        {summary.total > 0 ? (
          <>
            {summary.total} change{summary.total === 1 ? '' : 's'}
          </>
        ) : (
          'Changes'
        )}
      </button>

      <div className="flex w-full items-center justify-between gap-0.5 sm:w-auto sm:justify-start sm:order-2">
        <div className="relative">
          <IconButton
            label="Filter by department or team"
            active={filterOpen || departmentFilter.length > 0 || squadFilter.length > 0 || onlyChanged}
            onClick={() => setFilterOpen((open) => !open)}
          >
            <Filter size={15} />
          </IconButton>
          {filterOpen ? <FilterPopover onClose={() => setFilterOpen(false)} /> : null}
        </div>

        <div className="relative">
          <IconButton
            label="Colour the cards by a property"
            active={colorOpen || colorBy !== 'none'}
            onClick={() => setColorOpen((open) => !open)}
          >
            <Palette size={15} />
          </IconButton>
          {colorOpen ? <ColorPopover onClose={() => setColorOpen(false)} /> : null}
        </div>

        {/*
          Both canvases fold, so both get these. The team board is a tree of
          cards now rather than a list of boxes, and its own top row is as
          unreadable fully open as the chart's is.

          Collapsing re-frames the view as well — see `fitSignal`. Expanding
          deliberately does not: the fully expanded company is 90,000px wide,
          and framing that is a zoom nobody can read.
        */}
        {canvas ? (
          <>
            <IconButton label="Expand everything" onClick={view === 'teams' ? expandAllGroups : expandAll}>
              <ChevronsUpDown size={15} />
            </IconButton>
            <IconButton
              label={view === 'teams' ? 'Collapse everything' : 'Collapse back to the top of the org'}
              onClick={view === 'teams' ? collapseAllGroups : () => collapseToDepth(1)}
            >
              <ChevronsDownUp size={15} />
            </IconButton>
            <IconButton
              label={showMinimap ? 'Hide minimap' : 'Show minimap'}
              active={showMinimap}
              onClick={toggleMinimap}
            >
              <MapIcon size={15} />
            </IconButton>
          </>
        ) : null}

        <div className="mx-1 h-7 w-px bg-[var(--color-line)]" />

        {/* Importing and exporting a scenario now live in the scenario menu,
            beside New and Duplicate — the other ways of getting a plan. This
            one stays: it is a picture of the view, not of the plan. */}
        <IconButton
          label="Save a 1920×1080 PNG of this view"
          onClick={() => void exportPng()}
          disabled={exporting}
        >
          <ImageDown size={15} className={cn(exporting && 'animate-pulse')} />
        </IconButton>

        {/*
          Hosted, the org is published to Drive by whoever has Snowflake and read
          by everyone else, so a refresh button is a control nobody but that one
          person can use. Offering it and refusing is worse than not offering it.
        */}
        {snowflakeConfigured ? (
          <IconButton
            label="Refresh the org from Snowflake"
            onClick={() => void refreshOrg()}
            disabled={refreshing}
          >
            <RefreshCw size={15} className={cn(refreshing && 'animate-spin')} />
          </IconButton>
        ) : null}

        <IconButton
          label={nextTheme(theme) === 'light' ? 'Light mode' : 'Dark mode'}
          onClick={() => setTheme(nextTheme(theme))}
        >
          {resolveTheme(theme) === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </IconButton>

        {/*
          Only where there is something to sign out of. A session lasts a working
          day, so this is the way to end one early -- a shared screen, or somebody
          else's turn at the keyboard.

          It deliberately does not clear what the tab remembers: which scenario
          was open, what was expanded, what was filtered all stay, so signing
          back in resumes rather than restarts.
        */}
        {session ? (
          <IconButton label={`Sign out ${session.name}`} onClick={signOut}>
            <LogOut size={15} />
          </IconButton>
        ) : null}
      </div>
    </header>
  );
}

function ViewTab({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium transition-colors',
        active
          ? 'bg-[var(--color-surface)] text-[var(--color-ink)] shadow-sm'
          : 'text-[var(--color-ink-3)] hover:text-[var(--color-ink-2)]',
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/**
 * Which property paints the cards. A radio rather than checkboxes: a card has
 * one background, so colouring by two things at once is not a thing.
 */
function ColorPopover({ onClose }: { onClose: () => void }) {
  const colorBy = useAppStore((s) => s.colorBy);
  const setColorBy = useAppStore((s) => s.setColorBy);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close colour options"
        className="fixed inset-0 z-10 cursor-default"
        onClick={onClose}
      />
      <div
        role="radiogroup"
        aria-label="Colour cards by"
        className="absolute top-10 right-0 z-20 w-64 rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-panel)]"
      >
        <p className="px-2 pt-1 pb-2 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
          Colour cards by
        </p>
        {COLOR_BY_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--color-surface-2)]"
          >
            <input
              type="radio"
              name="color-by"
              value={option.value}
              checked={colorBy === option.value}
              onChange={() => setColorBy(option.value)}
              className="mt-0.5 accent-[var(--color-brand)]"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] text-[var(--color-ink)]">{option.label}</span>
              <span className="block text-[11px] text-[var(--color-ink-3)]">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </>
  );
}

function FilterPopover({ onClose }: { onClose: () => void }) {
  const stats = useAppStore((s) => s.stats);
  const squads = useAppStore((s) => s.squads.squads);
  const departmentFilter = useAppStore((s) => s.departmentFilter);
  const squadFilter = useAppStore((s) => s.squadFilter);
  const onlyChanged = useAppStore((s) => s.onlyChanged);
  const setDepartmentFilter = useAppStore((s) => s.setDepartmentFilter);
  const setSquadFilter = useAppStore((s) => s.setSquadFilter);
  const setOnlyChanged = useAppStore((s) => s.setOnlyChanged);

  const toggle = (department: string) => {
    setDepartmentFilter(
      departmentFilter.includes(department)
        ? departmentFilter.filter((d) => d !== department)
        : [...departmentFilter, department],
    );
  };

  const toggleSquad = (id: string) => {
    setSquadFilter(squadFilter.includes(id) ? squadFilter.filter((s) => s !== id) : [...squadFilter, id]);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close filters"
        className="fixed inset-0 z-10 cursor-default"
        onClick={onClose}
      />
      <div className="absolute top-10 right-0 z-20 w-64 rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-panel)]">
        <p className="px-2 pt-1 pb-2 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
          Highlight
        </p>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px] hover:bg-[var(--color-surface-2)]">
          <input
            type="checkbox"
            checked={onlyChanged}
            onChange={(event) => setOnlyChanged(event.target.checked)}
            className="accent-[var(--color-brand)]"
          />
          Only people in this scenario
        </label>

        {/*
         * A real group, not a heading over a list: teams are derived from the
         * HRIS team column, which in most orgs repeats the department names, so
         * "Finance" is now routinely a checkbox in both sections. Without the
         * grouping there is nothing to tell a screen reader — or a test — which
         * of the two identical labels it has hold of.
         */}
        <fieldset className="min-w-0">
          <legend className="px-2 pt-3 pb-2 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
            Departments
          </legend>
          <div className="max-h-64 space-y-0.5 overflow-y-auto pr-1">
            {(stats?.departments ?? []).map((department) => (
              <label
                key={department.department}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px] hover:bg-[var(--color-surface-2)]"
              >
                <input
                  type="checkbox"
                  checked={departmentFilter.includes(department.department)}
                  onChange={() => toggle(department.department)}
                  className="accent-[var(--color-brand)]"
                />
                <span className="flex-1 truncate">{department.department}</span>
                <Badge>{department.headcount}</Badge>
              </label>
            ))}
          </div>
        </fieldset>

        {squads.length > 0 ? (
          <fieldset className="min-w-0">
            <legend className="px-2 pt-3 pb-2 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
              Teams
            </legend>
            <div className="max-h-52 space-y-0.5 overflow-y-auto pr-1">
              {squads.map((squad) => (
                <label
                  key={squad.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px] hover:bg-[var(--color-surface-2)]"
                >
                  <input
                    type="checkbox"
                    checked={squadFilter.includes(squad.id)}
                    onChange={() => toggleSquad(squad.id)}
                    className="accent-[var(--color-brand)]"
                  />
                  <span className="flex-1 truncate">{squad.name}</span>
                  <Badge>{squad.members.length}</Badge>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {departmentFilter.length > 0 || squadFilter.length > 0 || onlyChanged ? (
          <button
            type="button"
            onClick={() => {
              setDepartmentFilter([]);
              setSquadFilter([]);
              setOnlyChanged(false);
            }}
            className="mt-2 w-full rounded-lg px-2 py-1.5 text-left text-[12px] text-[var(--color-brand)] hover:bg-[var(--color-surface-2)]"
          >
            Clear highlighting
          </button>
        ) : null}
      </div>
    </>
  );
}
